<template>
  <div class="rs-shell" style="margin-top: 20px">

    <!-- Marketing pitch for tenants without reports access -->
    <div v-if="!hasReportsAccess" class="rd-upsell">
      <div class="rd-upsell-icon-wrap">
        <i class="fa fa-bar-chart rd-upsell-icon"></i>
      </div>
      <h2 class="rd-upsell-headline">Unlock the Full Reports Suite</h2>
      <p class="rd-upsell-sub">
        Gain deep visibility into your fleet operations with our comprehensive reporting package.
      </p>
      <ul class="rd-upsell-list">
        <li>
          <i class="fa fa-check-circle"></i>
          <span><strong>On-Time Performance</strong> — track booking accuracy against real-world actuals</span>
        </li>
        <li>
          <i class="fa fa-check-circle"></i>
          <span><strong>Driver &amp; Payroll Reports</strong> — daily and multi-day summaries ready for export</span>
        </li>
        <li>
          <i class="fa fa-check-circle"></i>
          <span><strong>Vehicle Utilization</strong> — planned vs. actual usage across your entire fleet</span>
        </li>
        <li>
          <i class="fa fa-check-circle"></i>
          <span><strong>Mileage &amp; Billing</strong> — per-booking breakdowns for accurate client invoicing</span>
        </li>
      </ul>
      <router-link
        :to="{ name: 'support_ticket_create', query: { category: 'reports', subject: 'I\'d like to sign up for the report suite' } }"
        class="btn rd-upsell-cta"
      >
        <i class="fa fa-ticket"></i> Open a support ticket
      </router-link>
    </div>

    <!-- Main layout: nav + content -->
    <div v-else class="rs-layout">

      <!-- Collapseable left nav -->
      <div v-if="!horizonNavActive" class="rs-nav-wrapper" :class="{ 'rs-nav-collapsed': navCollapsed }">
        <div class="rs-nav-inner">

          <!-- Dashboard overview link -->
          <router-link
            :to="{ name: 'report_dashboard' }"
            class="rs-nav-item rs-nav-home"
            :class="{ active: $route.name === 'report_dashboard' }"
          >
            <i class="fa fa-th-large rs-nav-home-icon"></i>
            Dashboard
          </router-link>

          <!-- Category accordion sections -->
          <div v-for="cat in visibleCategories" :key="cat.id" class="rs-nav-cat">
            <button
              class="rs-nav-cat-header"
              :class="{ open: openCats.includes(cat.id), 'cat-active': isCatActive(cat), 'rs-nav-cat-favorites': cat.id === 'favorites' }"
              @click="toggleCat(cat.id)"
            >
              <span class="rs-nav-cat-label">
                <i v-if="cat.id === 'favorites'" class="fa fa-star rs-nav-fav-icon"></i>
                {{ cat.label }}
              </span>
              <i class="fa rs-nav-cat-chevron" :class="openCats.includes(cat.id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
            <div
              class="rs-nav-cat-items"
              :style="{ maxHeight: openCats.includes(cat.id) ? '800px' : '0' }"
            >
              <div
                v-for="item in cat.items"
                :key="item.route"
                class="rs-nav-sub-row"
                :class="{ active: $route.name === item.route }"
              >
                <router-link
                  :to="{ name: item.route }"
                  class="rs-nav-sub-item"
                  :class="{ active: $route.name === item.route }"
                >
                  {{ item.name }}
                </router-link>
                <button
                  class="rs-nav-star"
                  :class="{ 'rs-nav-star--active': isFavorite(item.route) }"
                  :title="isFavorite(item.route) ? 'Unfavorite' : 'Favorite'"
                  @click.prevent="toggleFavorite(item.route)"
                >{{ isFavorite(item.route) ? '★' : '☆' }}</button>
              </div>
            </div>
          </div>

        </div>

        <button
          class="rs-nav-toggle"
          @click="toggleNav"
          :title="navCollapsed ? 'Expand menu' : 'Collapse menu'"
          :aria-label="navCollapsed ? 'Expand menu' : 'Collapse menu'"
        >
          <i class="fa" :class="navCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
        </button>
      </div>

      <!-- Page content -->
      <div class="rs-content">
        <router-view />
      </div>

    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { favoritesState, isFavorite, toggleFavorite } from '@/utils/report_favorites'
import horizonNavMixin from '@/nav/horizon_nav_mixin'

export default {
  name: 'report_shell',
  mixins: [horizonNavMixin],
  data() {
    return {
      navCollapsed: false,
      navUserToggled: false,
      openCats: [],
      favoritesState,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    tenant_type() { return this.user.types[this.user.tenant] },
    telematics_type() { return this.user.telematics_types[this.user.tenant] },
    features() { return this.user.features[this.user.tenant] || {} },
    isEffectiveAdmin() {
      return this.user.role === 'admin' && this.viewAs !== 'non-admin'
    },
    isFasttrak() { return this.tenant_type === 'FASTTRAK' },
    hasReportsAccess() {
      if (this.isCmFleet) return true
      if (this.isFasttrak) return false
      const r = this.user.role
      const noReportsRoles = ['dispatch', 'driver', 'booking-customer', 'customer-care', 'wash-bay']
      if (noReportsRoles.includes(r)) return false
      if (this.isEffectiveAdmin) return true
      if (r === 'garage') return !this.isTbn
      return !!this.features.views_reports_enabled
    },
    isTbn() { return this.tenant_type === 'TBN' },
    isCmFleet() { return this.tenant_type === 'CM-FLEET' },
    isParentTbn() { return this.isTbn && this.user.tenant.indexOf('parent') !== -1 },
    restrictedRole() {
      if (this.isEffectiveAdmin) return null
      if (this.isCmFleet) return null
      const r = this.user.role
      if (r === 'garage') return 'garage'
      if (this.isParentTbn && r !== 'customer-admin') return 'tbn-parent'
      if (r === 'manager') return 'manager'
      return null
    },
    showActuals() {
      if (this.restrictedRole === 'garage') return false
      if (this.isCmFleet) return false
      if (this.restrictedRole === 'manager' || this.restrictedRole === 'tbn-parent') return true
      return !!this.features.actuals_enabled
    },
    showPlanned() {
      if (this.restrictedRole === 'manager' || this.restrictedRole === 'tbn-parent') return false
      if (this.isCmFleet) return false
      return true
    },
    showOtp() {
      if (this.restrictedRole) return false
      return !!this.features.views_reports_otp_report_enabled || this.isCmFleet
    },
    showMisc() {
      if (this.restrictedRole === 'garage' || this.restrictedRole === 'manager') return false
      if (this.isCmFleet) return false
      return true
    },

    actualsItems() {
      if (this.restrictedRole === 'manager' || this.restrictedRole === 'tbn-parent') {
        if (this.isTbn) {
          return [
            { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report_tbn' },
            { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report_tbn' },
          ]
        }
        return [
          { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report' },
          { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report' },
        ]
      }
      if (this.isTbn) {
        return [
          { name: 'Daily OTP Report',       route: 'actuals_perf_report_tbn' },
          { name: 'Multiday OTP Report',    route: 'actuals_multiday_otp_report_tbn' },
          { name: 'Pickup/Dropoff Report',  route: 'actuals_billing_report_tbn' },
          { name: 'Start Events Report',    route: 'actuals_start_events_report_tbn' },
          { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report_tbn' },
          { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report_tbn' },
          { name: 'Payroll Summary Report', route: 'actuals_payroll_summary_report_tbn' },
        ]
      }
      const items = [
        { name: 'Daily OTP Report',       route: 'actuals_perf_report' },
        { name: 'Multiday OTP Report',    route: 'actuals_multiday_otp_report' },
        { name: 'Mileage By Booking',     route: 'actuals_mileage_by_booking_report' },
        { name: 'Billing Report',         route: 'actuals_billing_report' },
        { name: 'Vehicle Start/Stop',     route: 'actuals_vehicle_report' },
        { name: 'Driver Daily Report',    route: 'actuals_driver_payroll_report' },
        { name: 'Driver Multiday Report', route: 'actuals_driver_multiday_report' },
        { name: 'Payroll Summary Report', route: 'actuals_payroll_summary_report' },
      ]
      return items
    },
    plannedItems() {
      if (this.restrictedRole === 'garage') {
        return [{ name: 'Vehicle Utilization Weekly', route: 'planned_vehicle_weekly' }]
      }
      if (this.isTbn) {
        return [
          { name: 'Driver Utilization Daily',    route: 'planned_drivers_daily_tbn' },
          { name: 'Driver Utilization Weekly',   route: 'planned_drivers_weekly_util_tbn' },
          { name: 'Driver Utilization Monthly',  route: 'planned_driver_types_monthly_tbn' },
          { name: 'Driver Dispatch Weekly',      route: 'planned_drivers_weekly_dispatch_tbn' },
          { name: 'Vehicle Utilization Daily',   route: 'planned_vehicle_daily_tbn' },
          { name: 'Vehicle Utilization Weekly',  route: 'planned_vehicle_weekly_tbn' },
          { name: 'Vehicle Utilization Monthly', route: 'planned_vehicle_types_monthly_tbn' },
          { name: 'Vehicle Departure Board',     route: 'trip_sheet_tbn' },
          { name: 'Booking Dispatch Board',      route: 'dispatch_board_tbn' },
          { name: 'Booking Monthly View',        route: 'booking_monthly_view_tbn' },
        ]
      }
      return [
        { name: 'Driver Board',                route: 'driver_board' },
        { name: 'Driver Utilization Daily',    route: 'planned_drivers_daily' },
        { name: 'Driver Utilization Monthly',  route: 'planned_driver_types_monthly_cm' },
        { name: 'Vehicle Utilization Daily',   route: 'planned_vehicle_daily' },
        { name: 'Vehicle Utilization Weekly',  route: 'planned_vehicle_weekly' },
        { name: 'Vehicle Utilization Monthly', route: 'planned_vehicle_types_monthly' },
        { name: 'Vehicle Departure Board',     route: 'trip_sheet' },
      ]
    },
    otpItems() {
      const items = [
        { name: 'Route History',              route: 'vehicle_route_history' },
        { name: 'Route Addresses',            route: 'route_addresses' },
        { name: 'OTP Nightly Report History', route: 'report_otp' },
        { name: 'OTP Results History',        route: 'otp_history' },
      ]
      if (!this.isTbn) {
        items.splice(1, 0, { name: 'Route Assignments', route: 'route_assignments' })
      }
      return items
    },
    miscItems() {
      if (this.restrictedRole === 'tbn-parent') {
        return [{ name: 'Event Notes History', route: 'event_notes_list' }]
      }
      const items = []
      if (!this.isTbn) {
        items.push(
          { name: 'Unplanned Vehicle Activity', route: 'report_unplanned_vehicle' },
          { name: 'Unplanned Driver Activity',  route: 'report_unplanned_hos' },
        )
      }
      items.push({ name: 'Event Notes History', route: 'event_notes_list' })
      if (this.user.tenant === 'jeffersonlines-d637fdad') {
        items.unshift(
          { name: 'Bus Tracker',             route: 'bus_tracker' },
          { name: 'Arrive/Depart Dashboard', route: 'arrivals_departures_board' },
        )
      }
      if (this.isTbn || this.telematics_type === 'SAMSARA') {
        items.push({ name: 'HOS Event History', route: 'hos_history_report' })
      }
      if (this.isTbn && this.features.tbn_tyler_route_sync_enabled) {
        items.push({ name: 'Tyler Route Processor', route: 'tyler_route_processor' })
      }
      if (this.features.vehicle_status_management_enabled) {
        items.push({ name: 'VSM History', route: 'vehicle_status_history' })
      }
      if (this.features.views_reports_converter_enabled) {
        items.push({ name: 'Report Converter', route: 'report_converter' })
      }
      if (!this.isTbn && this.features.vehicle_status_management_maint_summary_enabled) {
        items.push({ name: 'Vehicle Maintenance Summary', route: 'vehicle_maint_summary' })
      }
      if (this.isTbn && this.user.tenant === 'mtrwestern-tbn-3db5da5g7') {
        items.push({ name: 'Hours By Client Report',  route: 'actuals_hours_by_client_report_tbn' })
        items.push({ name: 'Hours By Booking Report', route: 'actuals_hours_by_booking_report_tbn' })
      }
      if (this.isTbn && (this.user.tenant === 'anderson-759g468f' || this.user.role === 'admin')) {
        items.push({ name: 'Intrastate Booking Report', route: 'intrastate_booking_report_tbn' })
      }
      return items
    },
    visibleCategories() {
      const cats = []
      if (this.favoriteItems.length) cats.push({ id: 'favorites', label: 'Favorites', items: this.favoriteItems })
      if (this.showActuals) cats.push({ id: 'actuals', label: 'Actuals', items: this.actualsItems })
      if (this.showPlanned) cats.push({ id: 'planned', label: 'Planned', items: this.plannedItems })
      if (this.showOtp)     cats.push({ id: 'otp',     label: 'OTP',     items: this.otpItems })
      if (this.showMisc)    cats.push({ id: 'misc',    label: 'Misc',    items: this.miscItems })
      return cats
    },
    allItems() {
      return [
        ...this.actualsItems,
        ...this.plannedItems,
        ...this.otpItems,
        ...this.miscItems,
      ]
    },
    favoriteItems() {
      return this.allItems.filter(i => this.favoritesState.routes.includes(i.route))
    },
  },
  mounted() {
    this._onResize = () => {
      // Keep the nav responsive to window width unless the user has explicitly toggled it
      if (this.navUserToggled) return
      this.navCollapsed = window.innerWidth < 1600
    }
    window.addEventListener('resize', this._onResize)
    this._onResize()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler(routeName) {
        for (const cat of this.visibleCategories) {
          if (cat.items.some(item => item.route === routeName)) {
            if (!this.openCats.includes(cat.id)) {
              this.openCats.push(cat.id)
            }
            break
          }
        }
      },
    },
    visibleCategories(cats) {
      const validIds = cats.map(c => c.id)
      this.openCats = this.openCats.filter(id => validIds.includes(id))
    },
  },
  methods: {
    toggleNav() {
      this.navCollapsed = !this.navCollapsed
      this.navUserToggled = true
    },
    toggleCat(id) {
      const idx = this.openCats.indexOf(id)
      if (idx === -1) {
        this.openCats.push(id)
      } else {
        this.openCats.splice(idx, 1)
      }
    },
    isCatActive(cat) {
      return cat.items.some(item => item.route === this.$route.name)
    },
    isFavorite(route) {
      return isFavorite(route)
    },
    toggleFavorite(route) {
      toggleFavorite(route)
    },
  },
}
</script>

<style scoped>
/* ── Outer layout ───────────────────────────────────────────────────── */
.rs-layout {
  display: flex;
  align-items: flex-start;
}

/* ── Nav wrapper (handles collapse transition) ──────────────────────── */
.rs-nav-wrapper {
  position: relative;
  display: flex;
  flex-shrink: 0;
}

.rs-nav-inner {
  width: 250px;
  min-width: 250px;
  overflow: hidden;
  transition: width 0.22s ease, min-width 0.22s ease;
  background-color: #e8eef5;
  border-right: 2px solid #dee2e6;
  padding: 12px 0;
}

.rs-nav-collapsed .rs-nav-inner {
  width: 0;
  min-width: 0;
  border-right: none;
  padding: 0;
}

/* ── Dashboard home link ────────────────────────────────────────────── */
.rs-nav-home {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #333 !important;
  text-decoration: none !important;
  border-bottom: 2px solid #c8d8e8;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  transition: background 0.12s;
}

.rs-nav-home:hover {
  background-color: #d0dfee;
  text-decoration: none !important;
}

.rs-nav-home.active {
  background-color: var(--fi-rail-accent) !important;
  color: white !important;
  border-bottom-color: #f0f0f0;
}

.rs-nav-home-icon {
  margin-right: 7px;
  font-size: 12px;
}

/* ── Category accordion ─────────────────────────────────────────────── */
.rs-nav-cat {
  border-bottom: 1px solid #c8d8e8;
}

.rs-nav-cat:last-child {
  border-bottom: none;
}

.rs-nav-cat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 9px 16px 9px 14px;
  background: transparent;
  border: none;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #4a6785;
  cursor: pointer;
  white-space: nowrap;
  outline: none;
  transition: background 0.12s, color 0.12s;
}

.rs-nav-cat-header:hover {
  background-color: #d0dfee;
  color: var(--fi-rail-accent);
}

.rs-nav-cat-header.cat-active {
  color: var(--fi-rail-accent);
}

.rs-nav-cat-label {
  flex: 1;
  text-align: left;
}

.rs-nav-cat-chevron {
  font-size: 10px;
  margin-left: 4px;
  flex-shrink: 0;
}

/* ── Sub-items (expandable) ─────────────────────────────────────────── */
.rs-nav-cat-items {
  overflow: hidden;
  transition: max-height 0.22s ease;
  background-color: #f0f5fa;
}

.rs-nav-sub-row {
  display: flex;
  align-items: center;
  position: relative;
}

.rs-nav-sub-row:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 24px;
  right: 10px;
  bottom: 0;
  height: 1px;
  background-color: #c8d8e8;
}

.rs-nav-sub-row .rs-nav-sub-item {
  flex: 1;
  min-width: 0;
}

.rs-nav-star {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  color: #ccc;
  padding: 0 10px 0 4px;
  outline: none;
  transition: color 0.15s, transform 0.1s;
  opacity: 0;
}

.rs-nav-sub-row:hover .rs-nav-star {
  opacity: 1;
}

.rs-nav-star--active {
  color: #e6b800 !important;
  opacity: 1 !important;
}

.rs-nav-star:hover {
  color: #e6b800;
  transform: scale(1.2);
}

/* ── Favorites category header ──────────────────────────────────────── */
.rs-nav-cat-favorites {
  color: #7a5c00 !important;
  background-color: #fffbea !important;
}

.rs-nav-cat-favorites:hover {
  background-color: #fff3c4 !important;
}

.rs-nav-fav-icon {
  color: #e6b800;
  margin-right: 5px;
  font-size: 11px;
}

.rs-nav-sub-item {
  display: block;
  padding: 8px 16px 8px 24px;
  font-size: 13px;
  font-weight: 600;
  color: #333 !important;
  text-decoration: none !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-left: 3px solid transparent;
  transition: background 0.1s, border-color 0.1s;
}

.rs-nav-sub-item:hover {
  background-color: #dce8f5;
  text-decoration: none !important;
}

.rs-nav-sub-item.active {
  background-color: #dce8f5;
  border-left-color: var(--fi-rail-accent);
  color: var(--fi-rail-accent) !important;
  font-weight: 600;
}

/* ── Collapse toggle nub ────────────────────────────────────────────── */
.rs-nav-toggle {
  position: absolute;
  top: 12px;
  right: -20px;
  width: 20px;
  height: 36px;
  background: #e8eef5;
  border: 1px solid #c8d8e8;
  border-left: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a6785;
  font-size: 11px;
  padding: 0;
  z-index: 10;
  transition: background 0.15s;
}

.rs-nav-toggle:hover {
  background: #d0dfee;
  color: var(--fi-rail-accent);
}

.rs-nav-toggle:focus {
  outline: none;
}

/* ── Content area ───────────────────────────────────────────────────── */
.rs-content {
  flex: 1;
  min-width: 0;
  padding-left: 32px;
}

/* ── Upsell panel (reused from dashboard) ───────────────────────────── */
.rd-upsell {
  max-width: 640px;
  margin: 40px auto;
  text-align: center;
  padding: 40px 32px;
  border: 2px solid #b8d0e4;
  border-radius: 10px;
  background: linear-gradient(180deg, #f0f6fc 0%, #fff 100%);
}
.rd-upsell-icon-wrap { margin-bottom: 16px; }
.rd-upsell-icon { font-size: 48px; color: var(--fi-rail-accent); }
.rd-upsell-headline { font-size: 24px; font-weight: bold; color: #1a3a5c; margin-bottom: 10px; }
.rd-upsell-sub { font-size: 15px; color: #555; margin-bottom: 24px; }
.rd-upsell-list { list-style: none; padding: 0; margin: 0 0 28px; text-align: left; display: inline-block; }
.rd-upsell-list li { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; font-size: 14px; color: #333; }
.rd-upsell-list .fa-check-circle { color: var(--fi-rail-accent); font-size: 16px; margin-top: 2px; flex-shrink: 0; }
.rd-upsell-cta { background-color: var(--fi-rail-accent); color: #fff !important; font-size: 15px; font-weight: bold; padding: 10px 28px; border-radius: 6px; text-decoration: none; border: none; }
.rd-upsell-cta:hover { background-color: #2e6da4; color: #fff !important; text-decoration: none; }
.rd-upsell-cta .fa { margin-right: 6px; }
</style>
